import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eyebrow } from '@/components/Atoms';
import { LEAD_MAGNETS } from '@/data/leadMagnets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lead magnet capture — POSTs to /api/contact with a tagged source so future
 * email automation (Resend/SendGrid) can deliver the asset.
 */
function LeadMagnetCard({ magnet }) {
  const Icon = magnet.icon;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: email.split('@')[0],
        email,
        message: `Lead magnet request: ${magnet.title}`,
        source: `lead_magnet:${magnet.id}`,
      });
      toast.success(`On its way — we will send "${magnet.title}" to your inbox.`);
      setDone(true);
      setEmail('');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-soft p-8 flex flex-col" data-testid={`lead-magnet-${magnet.id}`}>
      <Icon size={26} strokeWidth={1.25} className="text-[#C9A24D]" />
      <h3 className="font-serif-display text-2xl mt-5 leading-tight">{magnet.title}</h3>
      <p className="text-[#1C1C1C]/75 leading-relaxed mt-3 text-sm flex-1">{magnet.body}</p>
      {done ? (
        <p className="mt-6 font-serif-italic italic text-[#C9A24D] text-sm">
          Thank you. We will be in touch.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-6 flex items-end gap-3 border-b border-[#1C1C1C]/20 pb-1 focus-within:border-[#C9A24D] transition-colors">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid={`lead-magnet-${magnet.id}-email`}
            className="flex-1 bg-transparent border-none outline-none py-2 text-sm"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid={`lead-magnet-${magnet.id}-submit`}
            className="text-xs uppercase tracking-[0.18em] text-[#C9A24D] hover:text-[#1C1C1C] pb-2 transition-colors"
          >
            {loading ? 'Sending\u2026' : magnet.cta}
          </button>
        </form>
      )}
    </div>
  );
}

export default function LeadMagnetsBlock() {
  return (
    <section className="py-24 lg:py-32" data-testid="lead-magnets-block">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl">
          <Eyebrow>Free Resources</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
            Practical guides<br />
            <span className="font-serif-italic italic text-[#C9A24D]">for service-business owners.</span>
          </h2>
          <p className="mt-6 text-lg text-[#1C1C1C]/75 leading-relaxed">
            Short, useful resources drawn from real client engagements. Enter your email
            and we will send the resource to your inbox.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {LEAD_MAGNETS.map((m) => (
            <LeadMagnetCard key={m.id} magnet={m} />
          ))}
        </div>
      </div>
    </section>
  );
}
